import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Collapse } from "reactstrap";
import classNames from "classnames";
import { docPermConstants } from "../../../_constants/permissionConstants";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import AlertModal from "../../_common/AlertModal";
import contractorActions from "../../../_actions/contractorActions";

export default function AdminContractorDetails({ contractorId, handleClick_edit, deleteContractor, permissions }) {
  const dispatch = useDispatch();
  const fetching = useSelector((state) => state.contractors.fetching);
  const contractor = useSelector(
    (state) => state.contractors.openContractorDetails[contractorId] || null
  );

  const [projList, setProjList] = useState([]);
  const [showAlert_deleteContractorConfirm, setShowAlert_deleteContractorConfirm] = useState(false);
  const [showAlert_cannotDelete, setShowAlert_cannotDelete] = useState(false);
  const [checkingLinks, setCheckingLinks] = useState(false);

  useEffect(() => {
    if (contractor)
      setProjList((contractor.projects || []).map((p) => ({ ...p, isExpanded: false })));
  }, [contractor]);

  const handleClick_expandContractorProjRow = (rowIndex) => (e) => {
    e.preventDefault();
    setProjList((prev) =>
      prev.map((p, i) => (i === rowIndex ? { ...p, isExpanded: !p.isExpanded } : p))
    );
  };

  const handleClick_delete = async () => {
    setCheckingLinks(true);
    const hasLinks = await dispatch(contractorActions.getHasLinks(contractorId));
    setCheckingLinks(false);
    if (hasLinks) {
      setShowAlert_cannotDelete(true);
    } else {
      setShowAlert_deleteContractorConfirm(true);
    }
  };

  const handleAlert_deleteContractorConfirm = (action) => () => {
    setShowAlert_deleteContractorConfirm(false);
    if (action === "accept") deleteContractor(contractor);
  };

  const renderContractorProjectRow = (project, index) => (
    <div
      key={`cpRowKey_${index}`}
      className={classNames("list-group-item py-1 pl-1", {
        "custom-table-selected": projList[index] && projList[index].isExpanded,
      })}
    >
      <div
        className="d-flex flex-row w-100 align-middle pointer-cursor"
        onClick={handleClick_expandContractorProjRow(index)}
      >
        <div className="px-1" style={{ width: "10%" }}>
          <FontAwesomeIcon
            icon={projList[index] && projList[index].isExpanded ? "caret-up" : "caret-down"}
            className="fa-fw"
          />
        </div>
        <div className="pr-1" style={{ width: "30%" }}>
          <span className={classNames({ "text-strong": projList[index] && projList[index].isExpanded })}>
            {project.fullEA}
          </span>
        </div>
        <div className="px-1" style={{ width: "40%" }}>
          <span>{project.coRtePm}</span>
        </div>
        <div className="pl-1" style={{ width: "20%" }}>
          <span>{project.isPrime ? "Prime" : "Sub"}</span>
        </div>
      </div>
      <Collapse isOpen={projList[index] && projList[index].isExpanded}>
        <div className="card mt-1">
          <div className="card-body p-2">
            <div className="row">
              <div className="col-md-12">
                <b>Proj. Work Description:</b>
              </div>
            </div>
            <div className="row">
              <div className="col-md-12">{project.workDescription}</div>
            </div>
            <div className="row">
              <div className="col-md-3">
                <b>Contact: </b>
              </div>
              <div className="col-md-9">
                {`${project.contactFullName || "(N/A)"}${
                  project.contactTitle ? ", " + project.contactTitle : ""
                }`}
              </div>
            </div>
            <div className="row">
              <div className="col-md-3">
                <b>Phone: </b>
              </div>
              <div className="col-md-9">{project.phone || "(N/A)"}</div>
            </div>
            <div className="row">
              <div className="col-md-3">
                <b>Address: </b>
              </div>
              <div className="col-md-9">{project.address || "(N/A)"}</div>
            </div>
          </div>
        </div>
      </Collapse>
    </div>
  );

  return (
    <React.Fragment>
      {contractor ? (
        <h3 className="card-title mx-0">{contractor.name}</h3>
      ) : fetching ? (
        <h5 className="text-muted">
          <FontAwesomeIcon icon="spinner" className="fa-fw mr-2" spin />
          Loading...
        </h5>
      ) : (
        <h5 className="text-muted">Select a contractor to view details</h5>
      )}
      {contractor && (
        <React.Fragment>
          <div className="row mx-0 pl-0">
            <div className="col pl-0">
              <ul className="list-group-flush border-bottom border-top mb-2 pl-0">
                <li className="list-group-item py-1 pl-1">
                  <div className="row">
                    <div className="col pr-1">
                      <b>Name:</b>
                    </div>
                    <div className="col pl-1">{contractor.name}</div>
                  </div>
                </li>
                <li className="list-group-item py-1 pl-1">
                  <div className="row">
                    <div className="col pr-1">
                      <b>Abbreviation:</b>
                    </div>
                    <div className="col pl-1">
                      {contractor.abbreviation || "(No abbrev. provided)"}
                    </div>
                  </div>
                </li>
                <li className="list-group-item py-1 pl-1">
                  <div className="row">
                    <div className="col pr-1">
                      <b>Is Joint Venture:</b>
                    </div>
                    <div className="col pl-1">
                      {contractor.isJointVenture ? "Yes" : "No"}
                    </div>
                  </div>
                </li>
                <li className="list-group-item py-1 pl-1">
                  <div className="row">
                    <div className="col pr-1">
                      <b>Labor Count (Verified):</b>
                    </div>
                    <div className="col pl-1">{contractor.laborCount}</div>
                  </div>
                </li>
                <li className="list-group-item py-1 pl-1">
                  <div className="row">
                    <div className="col pr-1">
                      <b>Equipment:</b>
                    </div>
                    <div className="col pl-1">{contractor.equipmentCount}</div>
                  </div>
                </li>
              </ul>
            </div>
          </div>
          <h5 className="text-muted mx-0 mt-1">Projects (Past/Ongoing)</h5>
          <div className="list-group-flush mx-0 pl-0 mb-0">
            <div className="list-group-item py-1 pl-1 bg-lightgray">
              <div className="d-flex flex-row w-100">
                <div className="pr-1" style={{ width: "10%" }}></div>
                <div className="px-1" style={{ width: "30%" }}>
                  <b>EA</b>
                </div>
                <div className="px-1" style={{ width: "40%" }}>
                  <b>PM</b>
                </div>
                <div className="pl-1" style={{ width: "20%" }}>
                  <b>Role</b>
                </div>
              </div>
            </div>
          </div>
          <div className="contractor-details-projects border">
            <div className="list-group-flush pl-0 mb-0">
              {projList.length > 0 ? (
                projList.map((p, index) => renderContractorProjectRow(p, index))
              ) : (
                <li className="list-group-item py-1 pl-1">
                  No projects for this contractor
                </li>
              )}
            </div>
          </div>
          <hr className="my-2" />
          <div className="form-row form-group ml-1">
            <button
              type="button"
              name="editContractor"
              id="editContractor"
              className="btn btn-secondary mr-2"
              onClick={handleClick_edit}
              disabled={!permissions.includes(docPermConstants.EDIT_LEVEL_2)}
            >
              Edit...
            </button>
            <button
              type="button"
              name="deleteContractor"
              id="deleteContractor"
              className="btn btn-danger"
              onClick={handleClick_delete}
              disabled={!permissions.includes(docPermConstants.EDIT_LEVEL_2) || checkingLinks}
            >
              {checkingLinks ? (
                <React.Fragment>
                  <FontAwesomeIcon icon="spinner" className="fa-fw mr-1" spin />
                  Checking...
                </React.Fragment>
              ) : "Delete"}
            </button>
          </div>
        </React.Fragment>
      )}
      {showAlert_cannotDelete && (
        <AlertModal
          alertTitle="Cannot Delete"
          alertTitleClass="bg-warning"
          alertTitleIcon="exclamation-triangle"
          alertText="This contractor cannot be deleted since it is referenced by other entities."
          acceptCaption="OK"
          acceptClass="secondary"
          provideReject={false}
          handleAlertAction={() => () => setShowAlert_cannotDelete(false)}
          showAlertModal={showAlert_cannotDelete}
          toggleAlertModal={() => setShowAlert_cannotDelete(false)}
        />
      )}
      {showAlert_deleteContractorConfirm && (
        <AlertModal
          alertTitle="Confirm Delete"
          alertTitleClass="bg-danger text-white"
          alertTitleIcon="exclamation-triangle"
          alertText={
            projList.length > 0
              ? `Are you sure you want to delete this contractor?\n\nNote: This contractor is associated with ${projList.length} project(s). Deleting will remove it from all associated projects.`
              : "Are you sure you want to delete this contractor?"
          }
          acceptCaption="Yes"
          acceptClass="danger"
          provideReject={true}
          rejectCaption="No"
          rejectClass="secondary"
          handleAlertAction={handleAlert_deleteContractorConfirm}
          showAlertModal={showAlert_deleteContractorConfirm}
          toggleAlertModal={() => setShowAlert_deleteContractorConfirm(false)}
        />
      )}
    </React.Fragment>
  );
}
